/**
 * Initialises MSAL from the auth config and wraps children in <MsalProvider>.
 *
 * MSAL is built once per browser session — re-initialising would clobber the
 * in-memory token cache. We memoise on `spa_client_id` + `authority` so a
 * config change forces a fresh instance (uncommon, useful in tests).
 *
 * Token cache is in-memory only (CLAUDE.md §9.1 — tokens never persisted in
 * localStorage). Silent renewal uses the iframe flow.
 */

import { useEffect, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { setUnauthorisedHandler } from "@/shared/api/apiFetch";
import { useAuthConfig } from "./useAuthConfig";

export function MsalBootstrap({ children }: PropsWithChildren) {
  const config = useAuthConfig();

  const instance = useMemo(() => {
    return new PublicClientApplication({
      auth: {
        clientId: config.spa_client_id,
        authority: config.authority,
        redirectUri: pickRedirectUri(config.redirect_uris),
      },
      cache: {
        cacheLocation: "memoryStorage",
        storeAuthStateInCookie: false,
      },
    });
  }, [config.spa_client_id, config.authority, config.redirect_uris]);

  // Wire the apiFetch 401 interceptor to MSAL. When the API rejects a
  // request as unauthenticated we drop the in-memory MSAL session — the
  // token has either drifted, the account was revoked server-side, or
  // the user's session expired. We clear silently rather than triggering
  // a popup because the user has already been logged out by definition.
  useEffect(() => {
    setUnauthorisedHandler(() => {
      void instance.clearCache();
    });
    return () => setUnauthorisedHandler(null);
  }, [instance]);

  return <MsalProvider instance={instance}>{children}</MsalProvider>;
}

function pickRedirectUri(allowed: string[]): string {
  if (typeof window === "undefined") return allowed[0] ?? "";
  const origin = window.location.origin;
  // Prefer an exact origin match, fall back to anything starting with origin,
  // then to the first allowed value.
  return (
    allowed.find((u) => u === origin) ??
    allowed.find((u) => u.startsWith(origin)) ??
    allowed[0] ??
    origin
  );
}
